import React, { useState } from "react";
import { Search, Package, CheckCircle2, Clock, Truck, ChefHat, XCircle } from "lucide-react";
import { trackOrder } from "../lib/api";
import { inr } from "../lib/format";

const STEPS = [
  { key: "Received", label: "Order Received", icon: CheckCircle2 },
  { key: "Baking", label: "Baking", icon: ChefHat },
  { key: "Out for delivery", label: "Out for Delivery", icon: Truck },
  { key: "Delivered", label: "Delivered", icon: Package },
];

const TrackOrder = () => {
  const [orderId, setOrderId] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) return;
    setLoading(true); setError(""); setOrder(null);
    try {
      const data = await trackOrder(orderId.trim());
      setOrder(data);
    } catch (err) {
      setError("We couldn't find that order. Please check your Order ID and try again.");
    } finally {
      setLoading(false);
    }
  };

  const cancelled = order?.status === "Cancelled";
  const currentStep = order ? STEPS.findIndex((s) => s.key === order.status) : -1;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-12">
      <div className="text-center mb-8">
        <h1 className="font-display text-4xl font-bold text-[#3A2417]">Track Your Order</h1>
        <p className="text-[#B07A5C] mt-2">Enter your Order ID (e.g. GT123456) to see its summary & status.</p>
      </div>

      <form onSubmit={search} className="flex gap-3 max-w-lg mx-auto">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B07A5C]" size={18} />
          <input value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Enter Order ID"
            className="w-full h-12 pl-10 pr-4 rounded-full bg-white border border-[#EADBCB] outline-none focus:border-[#D46A8A]" />
        </div>
        <button type="submit" disabled={loading} className="h-12 px-6 rounded-full bg-[#D46A8A] text-white font-medium hover:bg-[#c25a7b] transition-colors disabled:opacity-60">
          {loading ? "Searching..." : "Track"}
        </button>
      </form>

      {error && <p className="text-center text-red-600 mt-6">{error}</p>}

      {order && (
        <div className="mt-10 space-y-6">
          {/* Status timeline */}
          <div className="bg-white rounded-2xl p-6 bakery-shadow">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-sm text-[#B07A5C]">Order ID</div>
                <div className="font-display text-xl font-bold text-[#D46A8A]">{order.id}</div>
              </div>
              <span className={`text-sm px-3 py-1.5 rounded-full ${cancelled ? "bg-red-50 text-red-600" : "bg-[#FCEFE3] text-[#D46A8A]"}`}>{order.status}</span>
            </div>

            {cancelled ? (
              <div className="flex items-center gap-3 text-red-600"><XCircle size={22} /> This order was cancelled.</div>
            ) : (
              <div className="flex items-center justify-between">
                {STEPS.map((s, i) => {
                  const done = i <= currentStep;
                  return (
                    <React.Fragment key={s.key}>
                      <div className="flex flex-col items-center text-center flex-1">
                        <div className={`w-11 h-11 rounded-full grid place-items-center ${done ? "bg-[#6BA84F] text-white" : "bg-[#F6E7DA] text-[#B07A5C]"}`}>
                          <s.icon size={20} />
                        </div>
                        <span className={`text-xs mt-2 ${done ? "text-[#3A2417] font-medium" : "text-[#B07A5C]"}`}>{s.label}</span>
                      </div>
                      {i < STEPS.length - 1 && <div className={`h-1 flex-1 rounded ${i < currentStep ? "bg-[#6BA84F]" : "bg-[#F0E4D6]"}`} />}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="bg-white rounded-2xl p-6 bakery-shadow">
            <h3 className="font-display text-lg font-semibold text-[#3A2417] mb-4">Order Summary</h3>
            <div className="space-y-3">
              {order.items?.map((i, idx) => (
                <div key={`${i.id}-${idx}`} className="flex gap-3 items-center">
                  {i.image && <img src={i.image} alt={i.name} className="w-12 h-12 rounded-lg object-cover" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#3A2417]">{i.name}</div>
                    <div className="text-xs text-[#B07A5C]">{i.flavor} · {i.weight} × {i.qty}{i.message ? ` · "${i.message}"` : ""}</div>
                  </div>
                  <div className="text-sm font-medium text-[#3A2417]">{inr(i.price * i.qty)}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-[#EADBCB] mt-4 pt-4 space-y-1.5 text-sm">
              <div className="flex justify-between text-[#6B4226]"><span>Delivery to</span><span className="text-right">{order.name} · {order.city}</span></div>
              <div className="flex justify-between text-[#6B4226]"><span>Delivery date</span><span>{order.date} ({order.slot})</span></div>
              <div className="flex justify-between font-display text-lg font-bold text-[#3A2417] pt-2"><span>Total</span><span>{inr(order.total)}</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackOrder;
